

import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Select, Option,
  Grid, Card, Avatar, Chip
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';
import dayjs from 'dayjs';

const Auditlog = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, eventTypeFilter, logs]);

  const fetchLogs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) {
      setLogs(data);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    const lowerSearch = search.toLowerCase();
    const result = logs.filter((log) => {
      const matchesEvent = eventTypeFilter ? log.event_type === eventTypeFilter : true;
      const matchesSearch = search
        ? (log.target?.toLowerCase().includes(lowerSearch) ||
           JSON.stringify(log.metadata).toLowerCase().includes(lowerSearch))
        : true;
      return matchesEvent && matchesSearch;
    });
    setFilteredLogs(result);
  };

  const eventTypes = [...new Set(logs.map((log) => log.event_type))];

  // Helper functions
  const getEventInitials = (eventType) => {
    return eventType?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'AL';
  };

  const getEventColor = (eventType) => {
    const colors = {
      'CREATE': '#28a745',
      'UPDATE': '#17a2b8', 
      'DELETE': '#dc3545',
      'LOGIN': '#6c757d',
      'LOGOUT': '#6c757d',
      'VIEW': '#ffc107',
      'EXPORT': '#e83e8c',
      'IMPORT': '#fd7e14'
    };
    const upperType = eventType?.toUpperCase();
    for (const [key, color] of Object.entries(colors)) {
      if (upperType?.includes(key)) {
        return color;
      }
    }
    return '#00A884';
  };

  const getSeverityLevel = (eventType) => {
    const upperType = eventType?.toUpperCase();
    if (upperType?.includes('DELETE') || upperType?.includes('REMOVE')) return 'High';
    if (upperType?.includes('CREATE') || upperType?.includes('UPDATE')) return 'Medium';
    if (upperType?.includes('LOGIN') || upperType?.includes('LOGOUT')) return 'Low';
    return 'Info';
  };

  const getSeverityColor = (level) => {
    switch (level) {
      case 'High': return 'danger';
      case 'Medium': return 'warning';
      case 'Low': return 'neutral';
      default: return 'primary';
    }
  };

  // Statistics
  const totalLogs = logs.length;
  const todayLogs = logs.filter(l => dayjs(l.created_at).isSame(dayjs(), 'day')).length;
  const weekLogs = logs.filter(l => dayjs(l.created_at).isAfter(dayjs().subtract(7, 'days'))).length;
  const highSeverityLogs = logs.filter(l => getSeverityLevel(l.event_type) === 'High').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Filters Loading */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}><LoadingSkeleton height="40px" /></Grid>
            <Grid xs={12} md={3}><LoadingSkeleton height="40px" /></Grid>
          </Grid>
        </Box>
        
        {/* Table Loading */}
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Audit Log Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            history
          </span>
          Audit Log Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Log Entries"
              value={totalLogs}
              icon="history"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Today's Activity"
              value={todayLogs}
              icon="today"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="This Week"
              value={weekLogs}
              icon="date_range"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="High Priority"
              value={highSeverityLogs}
              icon="priority_high"
              gradientColors={['#dc3545', '#c82333']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Filters Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ mb: 2 }}>
          <Typography 
            level="h4" 
            sx={{ 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              filter_alt
            </span>
            Filter & Search
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <EnhancedInput
              label="Search Logs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by target, metadata, or details..."
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  search
                </span>
              }
            />
          </Grid>
          
          <Grid xs={12} md={3}>
            <Box sx={{ mb: 2 }}>
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Event Type</Typography>
              <Select
                placeholder="All Event Types"
                value={eventTypeFilter}
                onChange={(e, value) => setEventTypeFilter(value || '')}
                sx={{ 
                  '&:focus-within': { 
                    borderColor: 'success.500', 
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' 
                  }
                }}
              >
                <Option value="">All Event Types</Option>
                {eventTypes.map((type) => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Box>
          </Grid>
          
          <Grid xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'end', height: '100%', pb: 2 }}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Showing {filteredLogs.length} of {totalLogs} entries
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Audit Log Table */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            list_alt
          </span>
          System Activity Log ({filteredLogs.length})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'event_info', label: 'Event', width: 200 },
            { key: 'timestamp', label: 'Timestamp', width: 180 },
            { key: 'user_info', label: 'User Role', width: 120 },
            { key: 'target_info', label: 'Target', width: 200 },
            { key: 'severity', label: 'Severity', width: 120 },
            { key: 'metadata_info', label: 'Details', width: 250 }
          ]}
          rows={filteredLogs.map((log, index) => ({
            event_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getEventColor(log.event_type),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    width: 32,
                    height: 32
                  }}
                >
                  {getEventInitials(log.event_type)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {log.event_type || 'Unknown Event'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    ID: {log.id}
                  </Typography>
                </Box>
              </Box>
            ),
            timestamp: (
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  {dayjs(log.created_at).format('MMM D, YYYY')}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  {dayjs(log.created_at).format('h:mm A')}
                </Typography>
              </Box>
            ),
            user_info: (
              <Chip 
                size="sm" 
                variant="soft" 
                color="neutral"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    person
                  </span>
                }
              >
                {log.user_role || 'Unknown'}
              </Chip>
            ),
            target_info: (
              <Typography 
                level="body-sm" 
                sx={{ 
                  fontFamily: 'monospace',
                  bgcolor: 'neutral.50',
                  p: 0.5,
                  borderRadius: 'sm',
                  fontSize: '0.75rem'
                }}
              >
                {log.target || 'N/A'}
              </Typography>
            ),
            severity: (
              <Chip 
                size="sm" 
                variant="soft" 
                color={getSeverityColor(getSeverityLevel(log.event_type))}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getSeverityLevel(log.event_type) === 'High' ? 'priority_high' : 
                     getSeverityLevel(log.event_type) === 'Medium' ? 'warning' :
                     getSeverityLevel(log.event_type) === 'Low' ? 'info' : 'circle'}
                  </span>
                }
              >
                {getSeverityLevel(log.event_type)}
              </Chip>
            ),
            metadata_info: (
              <Typography 
                level="body-xs" 
                sx={{ 
                  fontFamily: 'monospace',
                  bgcolor: 'neutral.50',
                  p: 1,
                  borderRadius: 'sm',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 230,
                  fontSize: '0.7rem'
                }}
                title={JSON.stringify(log.metadata, null, 2)}
              >
                {log.metadata ? JSON.stringify(log.metadata) : 'No metadata'}
              </Typography>
            )
          }))}
        />
      </Box>
    </Box>
  );
};

export default Auditlog;